import { DETAIL_ROW_VARIABLES } from 'lib';
import React from 'react';
import useVariableInsertion from './useVariableInsertion';
import { Btn, GroupLabel, IconBtn, Icons, Inset, Note, T, Tag, TextInput } from './ui';

/**
 * The campaign's detail card, as fields.
 *
 * The card under the email's body used to be four rows written in code — Event Date, Your Team,
 * Your Current Rank, Your Next Action — with the labels fixed and the values wired to fixed tags.
 * A campaign about a tournament wants "Venue" and "Tip-off"; one about a streak wants "Games
 * Played". Both got the same four, because that is what the template said.
 *
 * So the rows moved onto the campaign, and this is where they are written — the only place the
 * builder states what the card says. Two text inputs per row and nothing else: no rich text, no
 * markup, no per-row destination; the one link is the action link below the rows. The renderer
 * escapes both halves, so what an operator types is what a fan reads.
 *
 * Its own file rather than another section inside `CampaignBuilder`, which is already 745 lines and
 * holds the audience, the destination and the scheduler's inputs. A list with its own add, remove,
 * reorder and validation is the kind of thing that makes a long file unreadable, and it has a test
 * of its own.
 */

/** Matches the endpoint, which refuses a seventh row rather than truncating. */
export const MAX_ROWS = 6;

const EMPTY_ROW = { label: '', value: '', linked: false };

/**
 * What is wrong with a row, in the words the endpoint would use.
 *
 * Checked here as well as there because the server's refusal arrives after a save an operator
 * thought had worked, and the row at fault is three fields up the page by then. The endpoint stays
 * the authority — this is the earlier of two answers, not the only one.
 */
export function rowProblem(row) {
  const label = (row.label || '').trim();
  const value = (row.value || '').trim();

  if (!label && !value) return 'This row is empty. Fill it in or remove it.';
  if (!label) return 'This row has a value but no label.';
  if (!value) return 'This row has a label but nothing beside it.';
  if (/[{}]/.test(label)) return 'Labels are plain text. Put personalization tags in the value.';

  return null;
}

/**
 * Splits stored rows into the card's facts and its one action link.
 *
 * Stored as one list, as it always was: the action link is the row marked `linked`, and the server
 * already allows at most one. So no campaign changes shape. One saved with a linked row in the
 * middle shows it here as its action link, and saves it last.
 */
export function splitRows(rows = []) {
  const action = rows.find((row) => row.linked) || null;
  const facts = rows.filter((row) => row !== action).map((row) => ({ ...row, linked: false }));

  return { facts, action };
}

/** The stored list: facts in order, then the action link, which always draws last. */
export function joinRows(facts, action) {
  return [...facts, ...(action ? [{ ...action, linked: true }] : [])];
}

/*
  The card's facts, and one optional action link.

  Every row used to carry a "Link this value to the campaign's destination" toggle. A linked row is
  not a link to something about that row: it is a second copy of the campaign's button, so a linked
  date, team or rank sent a fan who tapped "Sat 12 Oct" or "42" to a FanCam challenge. Only an
  action reads true as that link, and the design draws exactly one: the card's last row, in link
  blue. So facts are never links, and the link is a line of its own that always goes where the
  button goes.
*/
/** A row with nothing typed in either half: new, not wrong. */
const isBlank = (row) => !(row.label || '').trim() && !(row.value || '').trim();

/*
  `revealEmpty`: whether a blank row says it is empty.

  Every new row said "This row is empty. Fill it in or remove it." the moment it appeared, under
  placeholders that read like a filled-in date, so a fresh card looked both full and broken (QA). A
  blank row is now quiet until the operator tries to save or send; a half-filled one still says what
  is missing straight away.
*/
const DetailRowsEditor = ({ rows = [], onChange, busy, readOnlyReason, revealEmpty = false }) => {
  const readOnly = Boolean(readOnlyReason) || busy;
  const { facts, action } = splitRows(rows);
  const commit = (nextFacts, nextAction) => onChange(joinRows(nextFacts, nextAction));
  const full = rows.length >= MAX_ROWS;

  /*
    The same caret handling the template editor uses, over the facts' values and the action's text,
    so a chip lands in whichever field was last focused.
  */
  const names = [...facts.map((_row, index) => `row:${index}:value`), 'action:value'];
  const { refFor, rememberSelection, insertVariable } = useVariableInsertion({
    fields: Object.fromEntries([
      ...facts.map((row, index) => [names[index], row.value || '']),
      ['action:value', action?.value || '']
    ]),
    onChange: (next) => {
      commit(
        facts.map((row, index) => ({ ...row, value: next[names[index]] ?? row.value })),
        action ? { ...action, value: next['action:value'] ?? action.value } : action
      );
    },
    names
  });

  const update = (index, patch) =>
    commit(
      facts.map((row, at) => (at === index ? { ...row, ...patch } : row)),
      action
    );

  const remove = (index) =>
    commit(
      facts.filter((_row, at) => at !== index),
      action
    );

  /*
    Reorder by one place, with buttons rather than by dragging.

    The CMS carries no drag-and-drop library, and a list capped at six rows does not justify adding
    one, nor a hand-rolled HTML5 drag, which is more code than this and worse on a touchscreen and
    with a keyboard. The action link is not reordered: it is always last.
  */
  const move = (index, by) => {
    const to = index + by;
    if (to < 0 || to >= facts.length) return;

    const next = [...facts];
    [next[index], next[to]] = [next[to], next[index]];
    commit(next, action);
  };

  const actionProblem = action && !(isBlank(action) && !revealEmpty) ? rowProblem(action) : null;

  return (
    <Inset>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap'
        }}
      >
        <GroupLabel>Detail rows</GroupLabel>
        <span style={{ fontSize: 12, color: T.muted }}>
          {rows.length} of {MAX_ROWS}
        </span>
      </div>

      {readOnlyReason ? <Note>{readOnlyReason}</Note> : null}

      {rows.length === 0 ? (
        <Note>This campaign shows no detail card. Add a row to give it one.</Note>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {facts.map((row, index) => {
          const problem = isBlank(row) && !revealEmpty ? null : rowProblem(row);

          return (
            <div key={`row-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextInput
                  aria-label={`Row ${index + 1} label`}
                  placeholder="Label"
                  value={row.label || ''}
                  disabled={readOnly}
                  style={{ flex: '1 1 150px', minWidth: 0 }}
                  onChange={(event) => update(index, { label: event.target.value })}
                />
                <TextInput
                  aria-label={`Row ${index + 1} value`}
                  placeholder="Value"
                  ref={refFor(names[index])}
                  value={row.value || ''}
                  disabled={readOnly}
                  style={{ flex: '1 1 180px', minWidth: 0 }}
                  onChange={(event) => update(index, { value: event.target.value })}
                  onSelect={rememberSelection(names[index])}
                  onBlur={rememberSelection(names[index])}
                />

                <IconBtn
                  type="button"
                  title="Move up"
                  aria-label={`Move row ${index + 1} up`}
                  disabled={readOnly || index === 0}
                  onClick={() => move(index, -1)}
                >
                  <Icons.Chevron style={{ transform: 'rotate(180deg)' }} />
                </IconBtn>

                <IconBtn
                  type="button"
                  title="Move down"
                  aria-label={`Move row ${index + 1} down`}
                  disabled={readOnly || index === facts.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <Icons.Chevron />
                </IconBtn>

                <IconBtn
                  type="button"
                  title="Remove row"
                  aria-label={`Remove row ${index + 1}`}
                  disabled={readOnly}
                  onClick={() => remove(index)}
                >
                  ×
                </IconBtn>
              </div>

              {problem ? (
                <span style={{ fontSize: 12, color: T.red }}>{`Row ${index + 1}: ${problem}`}</span>
              ) : null}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Btn
          type="button"
          variant="outline"
          icon="Plus"
          disabled={readOnly || full}
          onClick={() => commit([...facts, { ...EMPTY_ROW }], action)}
        >
          Add row
        </Btn>
        {full ? (
          <span style={{ fontSize: 12, color: T.muted }}>
            {`A detail card holds at most ${MAX_ROWS} rows, the action link included.`}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
          marginTop: 6,
          paddingTop: 12,
          borderTop: `1px solid ${T.border}`
        }}
      >
        <GroupLabel>Action link (optional)</GroupLabel>

        {action ? (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextInput
                aria-label="Action link label"
                placeholder="Label"
                value={action.label || ''}
                disabled={readOnly}
                style={{ flex: '1 1 150px', minWidth: 0 }}
                onChange={(event) => commit(facts, { ...action, label: event.target.value })}
              />
              <TextInput
                aria-label="Action link text"
                placeholder="Link text"
                ref={refFor('action:value')}
                value={action.value || ''}
                disabled={readOnly}
                style={{ flex: '1 1 180px', minWidth: 0 }}
                onChange={(event) => commit(facts, { ...action, value: event.target.value })}
                onSelect={rememberSelection('action:value')}
                onBlur={rememberSelection('action:value')}
              />
              <IconBtn
                type="button"
                title="Remove the action link"
                aria-label="Remove the action link"
                disabled={readOnly}
                onClick={() => commit(facts, null)}
              >
                ×
              </IconBtn>
            </div>
            {actionProblem ? (
              <span style={{ fontSize: 12, color: T.red }}>{`Action link: ${actionProblem}`}</span>
            ) : null}
          </>
        ) : (
          <div>
            <Btn
              type="button"
              variant="outline"
              icon="Plus"
              disabled={readOnly || full}
              onClick={() => commit(facts, { ...EMPTY_ROW, linked: true })}
            >
              Add action link
            </Btn>
          </div>
        )}

        <Note>
          The last line of the card, drawn as a link, for example Your move → Post your best FanCam.
          It goes where the campaign&apos;s button goes, set by Button Destination below. The rows
          above are never links.
        </Note>
      </div>

      {/*
        The chips, under a heading of their own. They insert into whichever row value or action text
        was last focused.

        Hidden entirely with nothing to insert into. A disabled chip row under an empty list is a
        control that explains nothing.
      */}
      {rows.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 4 }}>
          <GroupLabel>Insert into a row value</GroupLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {DETAIL_ROW_VARIABLES.map((label) => (
              <Tag
                key={label}
                type="button"
                disabled={readOnly}
                onClick={() => insertVariable(label)}
              >
                {`+ ${label}`}
              </Tag>
            ))}
          </div>
        </div>
      ) : null}

      <Note>
        Each row is a label and its value, for example Tip-off → Sat 12 Oct, 7:30pm, or Your Team →{' '}
        {'{Team Name}'}. Labels are plain text. Values may mix words with the tags above, and a row
        whose value has nothing to say for a recipient is left out of their copy entirely.
      </Note>
    </Inset>
  );
};

export default DetailRowsEditor;
export { EMPTY_ROW };
